import React, { useState, useEffect } from 'react';
import { settingsAPI, placementSettingsAPI, feedbackTemplatesAPI, courseTypesAPI, icaSignatureAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Settings as SettingsIcon, Upload, Trash2, AlertTriangle, Image, Database, Shield, Mail, Clock, Save, Plus, BookOpen, X, FileSignature } from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { user: currentUser } = useAuth();
  const [logoUrl, setLogoUrl] = useState(null);
  const [icaSignatureData, setIcaSignatureData] = useState(null);
  const [icaSignerName, setIcaSignerName] = useState('');
  const [icaSignerDesignation, setIcaSignerDesignation] = useState('');
  // Contract branding (logo + sender info shown on contracts and letters)
  const [contractLogoUrl, setContractLogoUrl] = useState('');
  const [icaSignerCell, setIcaSignerCell] = useState('');
  const [icaPhone, setIcaPhone] = useState('');
  const [icaEmail, setIcaEmail] = useState('');
  const [icaWebsite, setIcaWebsite] = useState('');
  const [icaFacebook, setIcaFacebook] = useState('');
  const [icaAddress, setIcaAddress] = useState('');
  const [savingBranding, setSavingBranding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmStudents, setConfirmStudents] = useState(false);
  const [confirmRestaurants, setConfirmRestaurants] = useState(false);
  const [confirmFeedback, setConfirmFeedback] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [placementSettings, setPlacementSettings] = useState({
    assessments_per_placement: 4,
    assessment_interval_days: 30,
    email_initial_subject: 'Assessment Request: {student_name} - Assessment #{assessment_number}',
    email_initial_body: 'You have been asked to complete an industry assessment for the following student placement.',
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [courseTypes, setCourseTypes] = useState([]);
  const [newCourseType, setNewCourseType] = useState('');
  const [addingCourseType, setAddingCourseType] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchPlacementSettings();
    fetchCourseTypes();
  }, []);

  const fetchCourseTypes = async () => {
    try {
      const res = await courseTypesAPI.detail();
      setCourseTypes(res.data || []);
    } catch {}
  };

  const handleSetLength = async (name, course_length) => {
    try {
      const res = await courseTypesAPI.setLength(name, course_length);
      setCourseTypes(res.data || []);
      toast.success(`${name} is now a ${course_length} course`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update course length');
    }
  };

  const handleAddCourseType = async () => {
    const name = newCourseType.trim();
    if (!name) return;
    setAddingCourseType(true);
    try {
      await courseTypesAPI.add(name);
      await fetchCourseTypes();
      setNewCourseType('');
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add course type');
    } finally {
      setAddingCourseType(false);
    }
  };

  const handleRemoveCourseType = async (name) => {
    if (!window.confirm(`Remove course type "${name}"? Students currently assigned to it will keep their value until edited.`)) return;
    try {
      await courseTypesAPI.remove(name);
      await fetchCourseTypes();
      toast.success(`Removed "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove course type');
    }
  };

  const fetchPlacementSettings = async () => {
    try {
      const res = await placementSettingsAPI.get();
      setPlacementSettings(prev => ({ ...prev, ...res.data }));
    } catch {}
  };

  const handleSavePlacementSettings = async () => {
    setSavingSettings(true);
    try {
      await placementSettingsAPI.update(placementSettings);
      toast.success('Placement settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setLogoUrl(response.data.logo_url);
      setIcaSignatureData(response.data.ica_signature_data || null);
      setIcaSignerName(response.data.ica_signer_name || '');
      setIcaSignerDesignation(response.data.ica_signer_designation || '');
      setContractLogoUrl(response.data.contract_logo_url || '');
      setIcaSignerCell(response.data.ica_signer_cell || '');
      setIcaPhone(response.data.ica_phone || '');
      setIcaEmail(response.data.ica_email || '');
      setIcaWebsite(response.data.ica_website || '');
      setIcaFacebook(response.data.ica_facebook || '');
      setIcaAddress(response.data.ica_address || '');
    } catch (error) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleIcaSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await icaSignatureAPI.update({ ica_signature_data: reader.result });
        setIcaSignatureData(reader.result);
        toast.success('ICA signature uploaded');
      } catch {
        toast.error('Failed to upload signature');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveIcaSignerDetails = async () => {
    try {
      await icaSignatureAPI.update({
        ica_signer_name: icaSignerName,
        ica_signer_designation: icaSignerDesignation,
      });
      toast.success('ICA signatory details saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await icaSignatureAPI.update({
        contract_logo_url: contractLogoUrl || '',
        ica_signer_cell: icaSignerCell || '',
        ica_phone: icaPhone || '',
        ica_email: icaEmail || '',
        ica_website: icaWebsite || '',
        ica_facebook: icaFacebook || '',
        ica_address: icaAddress || '',
      });
      toast.success('Contract branding saved');
    } catch {
      toast.error('Failed to save branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleBrandingLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setContractLogoUrl(reader.result);
      try {
        await icaSignatureAPI.update({ contract_logo_url: reader.result });
        toast.success('Contract logo uploaded');
      } catch {
        toast.error('Failed to upload contract logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveIcaSignature = async () => {
    if (!window.confirm('Remove the ICA signature?')) return;
    try {
      await icaSignatureAPI.update({ ica_signature_data: '' });
      setIcaSignatureData(null);
      toast.success('ICA signature removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      const response = await settingsAPI.uploadLogo(file);
      setLogoUrl(response.data.logo_url);
      toast.success('Logo uploaded successfully');
      // Trigger a page refresh to update the sidebar
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the logo?')) return;

    try {
      await settingsAPI.deleteLogo();
      setLogoUrl(null);
      toast.success('Logo removed successfully');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handleResetStudents = async () => {
    if (!confirmStudents) {
      toast.error('Please confirm by checking the checkbox');
      return;
    }

    setResetting(true);
    try {
      const response = await settingsAPI.resetDatabase({ confirm: true, reset_type: 'students' });
      toast.success(`Deleted ${response.data.deleted.students} students and ${response.data.deleted.placements} placements`);
      setConfirmStudents(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset students');
    } finally {
      setResetting(false);
    }
  };

  const handleResetRestaurants = async () => {
    if (!confirmRestaurants) {
      toast.error('Please confirm by checking the checkbox');
      return;
    }

    setResetting(true);
    try {
      const response = await settingsAPI.resetDatabase({ confirm: true, reset_type: 'restaurants' });
      toast.success(`Deleted ${response.data.deleted.restaurants} restaurants and ${response.data.deleted.placements} placements`);
      setConfirmRestaurants(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset restaurants');
    } finally {
      setResetting(false);
    }
  };

  const handleResetFeedback = async () => {
    if (!confirmFeedback) {
      toast.error('Please confirm by checking the checkbox');
      return;
    }

    setResetting(true);
    try {
      const response = await settingsAPI.resetDatabase({ confirm: true, reset_type: 'feedback' });
      toast.success(`Deleted ${response.data.deleted.feedback_forms} feedback forms and ${response.data.deleted.feedback_submissions} submissions`);
      setConfirmFeedback(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset feedback');
    } finally {
      setResetting(false);
    }
  };

  const handleResetAll = async () => {
    if (!confirmAll) {
      toast.error('Please confirm by checking the checkbox');
      return;
    }

    setResetting(true);
    try {
      const response = await settingsAPI.resetDatabase({ confirm: true, reset_type: 'all' });
      toast.success(`Deleted all data: ${response.data.deleted.students} students, ${response.data.deleted.restaurants} restaurants, ${response.data.deleted.placements} placements`);
      setConfirmAll(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset database');
    } finally {
      setResetting(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Shield className="w-12 h-12 text-gray-300 mb-4" />
          <p className="text-gray-500">Admin access required to view this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="settings-page" className="space-y-6 animate-fade-in max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-ica-navy">Settings</h1>
          <p className="text-gray-500 mt-1">Manage application settings and data</p>
        </div>

        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <Image className="w-5 h-5" />
              Application Logo
            </CardTitle>
            <CardDescription>
              Upload a custom logo to display in the sidebar and login page
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {logoUrl ? (
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 border rounded-lg flex items-center justify-center bg-gray-50 p-2">
                  <img src={logoUrl} alt="App Logo" className="max-w-full max-h-full object-contain" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">Current logo uploaded</p>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Replace
                        </span>
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                    <Button variant="outline" size="sm" onClick={handleDeleteLogo} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-ica-gold transition-colors">
                <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <label className="cursor-pointer">
                  <span className="text-ica-navy font-medium hover:text-ica-gold">Click to upload logo</span>
                  <input
                    type="file"
                    data-testid="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-400 mt-2">PNG, JPG, SVG up to 2MB</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assessment & Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <Mail className="w-5 h-5" />
              Assessment & Email Settings
            </CardTitle>
            <CardDescription>
              Configure assessment intervals and the initial assessment-request email. The <strong>Assessment reminder</strong> subject &amp; body now live on the new <strong>Email Templates</strong> page (sidebar → Admin). Use placeholders: {'{student_name}'}, {'{restaurant_name}'}, {'{assessment_number}'}, {'{due_date}'}, {'{access_code}'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Interval Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium">Assessments Per Placement</Label>
                <input
                  data-testid="assessments-per-placement"
                  type="number"
                  min="1" max="12"
                  value={placementSettings.assessments_per_placement}
                  onChange={(e) => setPlacementSettings(s => ({ ...s, assessments_per_placement: parseInt(e.target.value) || 4 }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy"
                />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Assessment Interval (days)</Label>
                <input
                  data-testid="assessment-interval-days"
                  type="number"
                  min="7" max="365"
                  value={placementSettings.assessment_interval_days}
                  onChange={(e) => setPlacementSettings(s => ({ ...s, assessment_interval_days: parseInt(e.target.value) || 30 }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy"
                />
                <p className="text-xs text-gray-400 mt-1">Days between each assessment due date</p>
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Sender Email</Label>
                <input
                  data-testid="sender-email"
                  type="email"
                  value={placementSettings.sender_email || ''}
                  onChange={(e) => setPlacementSettings(s => ({ ...s, sender_email: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy"
                  placeholder="noreply@yourdomain.com"
                />
                <p className="text-xs text-gray-400 mt-1">Must be from a verified domain in Resend</p>
              </div>
            </div>

            {/* Initial Email Template */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <h3 className="font-medium text-gray-900 text-sm">Initial Assessment Email</h3>
              <div>
                <Label className="text-xs">Subject Line</Label>
                <input
                  data-testid="email-initial-subject"
                  type="text"
                  value={placementSettings.email_initial_subject}
                  onChange={(e) => setPlacementSettings(s => ({ ...s, email_initial_subject: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy"
                />
              </div>
              <div>
                <Label className="text-xs">Email Body</Label>
                <textarea
                  data-testid="email-initial-body"
                  rows={3}
                  value={placementSettings.email_initial_body}
                  onChange={(e) => setPlacementSettings(s => ({ ...s, email_initial_body: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ica-navy focus:ring-1 focus:ring-ica-navy resize-none"
                />
              </div>
            </div>

            {/* Reminder Email moved to the dedicated Email Templates page. */}

            <div className="flex justify-end">
              <Button
                data-testid="save-placement-settings-btn"
                onClick={handleSavePlacementSettings}
                disabled={savingSettings}
                
              >
                <Save className="w-4 h-4 mr-2" />
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contract Branding (logo + sender info embedded on every contract/letter) */}
        <Card data-testid="contract-branding-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <Image className="w-5 h-5" />
              Contract Branding & Sender Info
            </CardTitle>
            <CardDescription>
              These values appear at the top of every contract and on the Industry / Placement letters (logo + ICA contact block).
              Logo defaults to the bundled ICA mark; paste a public URL or upload an image to override.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Contract Logo URL</Label>
              <Input
                data-testid="contract-logo-url"
                value={contractLogoUrl}
                onChange={(e) => setContractLogoUrl(e.target.value)}
                placeholder="https://… or paste a base64 data URL"
              />
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    className="hidden"
                    onChange={handleBrandingLogoUpload}
                    data-testid="contract-logo-upload"
                  />
                  <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-ica-navy text-ica-navy hover:bg-ica-navy hover:text-white rounded cursor-pointer transition-colors">
                    <Upload className="w-3.5 h-3.5" /> Upload logo
                  </span>
                </label>
                {contractLogoUrl && (
                  <button
                    type="button"
                    data-testid="clear-contract-logo"
                    onClick={() => setContractLogoUrl('')}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Clear (use default)
                  </button>
                )}
              </div>
              {contractLogoUrl && (
                <div className="border rounded bg-gray-50 p-3 inline-block">
                  <img src={contractLogoUrl} alt="Contract logo preview" className="max-h-[80px] max-w-[260px]" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t">
              <div>
                <Label>ICA address</Label>
                <Input data-testid="ica-address" value={icaAddress} onChange={(e) => setIcaAddress(e.target.value)} placeholder="Farm Calenick, Zevenwacht Road, Kuils River" />
              </div>
              <div>
                <Label>ICA office phone</Label>
                <Input data-testid="ica-phone" value={icaPhone} onChange={(e) => setIcaPhone(e.target.value)} placeholder="+27 21 …" />
              </div>
              <div>
                <Label>ICA email</Label>
                <Input data-testid="ica-email" value={icaEmail} onChange={(e) => setIcaEmail(e.target.value)} placeholder="placements@icachef.co.za" />
              </div>
              <div>
                <Label>ICA Co-ordinator mobile</Label>
                <Input data-testid="ica-signer-cell" value={icaSignerCell} onChange={(e) => setIcaSignerCell(e.target.value)} placeholder="+27 …" />
              </div>
              <div>
                <Label>ICA website</Label>
                <Input data-testid="ica-website" value={icaWebsite} onChange={(e) => setIcaWebsite(e.target.value)} placeholder="www.icachef.co.za" />
              </div>
              <div>
                <Label>ICA Facebook</Label>
                <Input data-testid="ica-facebook" value={icaFacebook} onChange={(e) => setIcaFacebook(e.target.value)} placeholder="facebook.com/ICAChef.co.za" />
              </div>
            </div>

            <div>
              <Button
                data-testid="save-branding-btn"
                size="sm"
                onClick={handleSaveBranding}
                disabled={savingBranding}
                
              >
                <Save className="w-4 h-4 mr-1.5" /> {savingBranding ? 'Saving…' : 'Save branding'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ICA Signature for contracts */}
        <Card data-testid="ica-signature-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <FileSignature className="w-5 h-5" />
              ICA Signature (Contract Counter-Signature)
            </CardTitle>
            <CardDescription>
              Auto-stamped on every contract when a restaurant completes their signature.
              Upload a clean PNG/JPG of the authorised representative's signature, plus their name &amp; designation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Authorised Representative Name</Label>
                <Input
                  data-testid="ica-signer-name"
                  value={icaSignerName}
                  onChange={(e) => setIcaSignerName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  data-testid="ica-signer-designation"
                  value={icaSignerDesignation}
                  onChange={(e) => setIcaSignerDesignation(e.target.value)}
                  placeholder="e.g. Academic Head"
                />
              </div>
            </div>
            <div>
              <Button
                data-testid="save-ica-signer-btn"
                size="sm"
                onClick={handleSaveIcaSignerDetails}
                
              >
                <Save className="w-4 h-4 mr-1.5" /> Save Details
              </Button>
            </div>
            <div className="flex items-center gap-4 pt-3 border-t">
              <div className="border rounded bg-gray-50 p-2 min-w-[180px] h-[80px] flex items-center justify-center">
                {icaSignatureData ? (
                  <img src={icaSignatureData} alt="ICA signature" className="max-h-[64px] max-w-full" />
                ) : (
                  <span className="text-xs text-gray-400 italic">No signature uploaded</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={handleIcaSignatureUpload}
                    data-testid="ica-signature-upload"
                  />
                  <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-ica-navy text-ica-navy hover:bg-ica-navy hover:text-white rounded cursor-pointer transition-colors">
                    <Upload className="w-4 h-4" /> Upload Signature
                  </span>
                </label>
                {icaSignatureData && (
                  <Button
                    data-testid="remove-ica-signature-btn"
                    size="sm"
                    variant="ghost"
                    onClick={handleRemoveIcaSignature}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Course Types */}
        <Card data-testid="course-types-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ica-navy">
              <BookOpen className="w-5 h-5" />
              Course Types
            </CardTitle>
            <CardDescription>
              Course types available to students and placement cycles. Each type carries its course length, which drives the years of study a cycle can target.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {courseTypes.length === 0 && (
                <p className="text-sm text-gray-500">No course types yet. Add the first one below.</p>
              )}
              {courseTypes.map((ct) => (
                <div
                  key={ct.name}
                  data-testid={`course-type-chip-${ct.name}`}
                  className="flex items-center gap-3 rounded-md border border-ica-gold/30 bg-ica-gold/5 px-3 py-2"
                >
                  <span className="text-sm font-medium text-ica-navy flex-1 truncate">{ct.name}</span>
                  <select
                    data-testid={`course-type-length-${ct.name}`}
                    value={ct.course_length}
                    onChange={(e) => handleSetLength(ct.name, e.target.value)}
                    className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700"
                  >
                    {['1 Year', '2 Year', '3 Year'].map((l) => <option key={l} value={l}>{l} course</option>)}
                  </select>
                  <button
                    data-testid={`remove-course-type-${ct.name}`}
                    onClick={() => handleRemoveCourseType(ct.name)}
                    className="w-6 h-6 inline-flex items-center justify-center rounded-full hover:bg-red-100 hover:text-red-600 transition-colors text-gray-400"
                    title={`Remove ${ct.name}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="new-course-type-input"
                value={newCourseType}
                onChange={(e) => setNewCourseType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCourseType(); } }}
                placeholder="Add a new course type (e.g. Culinary Foundations)"
                className="flex-1"
              />
              <Button
                data-testid="add-course-type-btn"
                onClick={handleAddCourseType}
                disabled={addingCourseType || !newCourseType.trim()}
                
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Database Reset */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Database className="w-5 h-5" />
              Database Management
            </CardTitle>
            <CardDescription>
              Reset or clear data from the database. These actions cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Delete Students */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Delete All Students</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This will delete all students and their associated placements.
                  </p>
                  <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="confirm-students"
                        data-testid="confirm-delete-students"
                        checked={confirmStudents}
                        onCheckedChange={setConfirmStudents}
                      />
                      <Label htmlFor="confirm-students" className="text-sm text-gray-600">
                        I confirm I want to delete all students
                      </Label>
                    </div>
                  </div>
                  <Button
                    data-testid="delete-students-btn"
                    variant="destructive"
                    size="sm"
                    onClick={handleResetStudents}
                    disabled={!confirmStudents || resetting}
                    className="mt-3"
                  >
                    {resetting ? 'Deleting...' : 'Delete All Students'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Delete Restaurants */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Delete All Restaurants</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This will delete all restaurants and their associated placements.
                  </p>
                  <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="confirm-restaurants"
                        data-testid="confirm-delete-restaurants"
                        checked={confirmRestaurants}
                        onCheckedChange={setConfirmRestaurants}
                      />
                      <Label htmlFor="confirm-restaurants" className="text-sm text-gray-600">
                        I confirm I want to delete all restaurants
                      </Label>
                    </div>
                  </div>
                  <Button
                    data-testid="delete-restaurants-btn"
                    variant="destructive"
                    size="sm"
                    onClick={handleResetRestaurants}
                    disabled={!confirmRestaurants || resetting}
                    className="mt-3"
                  >
                    {resetting ? 'Deleting...' : 'Delete All Restaurants'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Delete Feedback Forms */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Delete All Feedback Forms</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This will delete all placement feedback forms and their submissions.
                  </p>
                  <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="confirm-feedback"
                        data-testid="confirm-delete-feedback"
                        checked={confirmFeedback}
                        onCheckedChange={setConfirmFeedback}
                      />
                      <Label htmlFor="confirm-feedback" className="text-sm text-gray-600">
                        I confirm I want to delete all feedback forms
                      </Label>
                    </div>
                  </div>
                  <Button
                    data-testid="delete-feedback-btn"
                    variant="destructive"
                    size="sm"
                    onClick={handleResetFeedback}
                    disabled={!confirmFeedback || resetting}
                    className="mt-3"
                  >
                    {resetting ? 'Deleting...' : 'Delete All Feedback Forms'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Reset All */}
            <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-red-900">Reset All Data</h3>
                  <p className="text-sm text-red-700 mt-1">
                    This will delete ALL students, restaurants, placements, and feedback forms. This action cannot be undone!
                  </p>
                  <div className="flex items-center gap-3 mt-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="confirm-all"
                        data-testid="confirm-delete-all"
                        checked={confirmAll}
                        onCheckedChange={setConfirmAll}
                      />
                      <Label htmlFor="confirm-all" className="text-sm text-red-800">
                        I confirm I want to delete ALL data
                      </Label>
                    </div>
                  </div>
                  <Button
                    data-testid="delete-all-btn"
                    variant="destructive"
                    onClick={handleResetAll}
                    disabled={!confirmAll || resetting}
                    className="mt-3"
                  >
                    {resetting ? 'Deleting...' : 'Reset All Data'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Settings;
